import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, forkJoin, map, of, shareReplay, switchMap } from 'rxjs';
import {
  AttachLabelRequest,
  CreateLabelDefinitionRequest,
  CreateLabelRequest,
  Label,
  LabelDefinition,
  ObjectLabel,
} from '../models/label.model';

const DEFAULT_LABEL_DEFINITIONS: CreateLabelDefinitionRequest[] = [
  {
    name: 'Color',
    access: 'PUBLIC',
    renderType: 'COLOR',
    selectionType: 'MULTIPLE',
    labels: [
      { name: 'Red',    priority: 1, color: '#ef4444', textColor: '#ffffff' },
      { name: 'Orange', priority: 2, color: '#f97316', textColor: '#ffffff' },
      { name: 'Yellow', priority: 3, color: '#eab308', textColor: '#ffffff' },
      { name: 'Green',  priority: 4, color: '#22c55e', textColor: '#ffffff' },
      { name: 'Blue',   priority: 5, color: '#3b82f6', textColor: '#ffffff' },
      { name: 'Purple', priority: 6, color: '#a855f7', textColor: '#ffffff' },
      { name: 'Pink',   priority: 7, color: '#ec4899', textColor: '#ffffff' },
      { name: 'Gray',   priority: 8, color: '#6b7280', textColor: '#ffffff' },
    ],
  },
  // NOTE: the 'Compliance' definition (Draft/Under review/Approved/…) is no longer
  // seeded here — it is system-managed, seeded by Java Flyway migration V085.
];

@Injectable({ providedIn: 'root' })
export class LabelService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/core/label';

  readonly refresh$ = new Subject<void>();

  // --- Label Definitions ---

  private definitionsCache$: Observable<LabelDefinition[]> | null = null;

  getLabelDefinitions(): Observable<LabelDefinition[]> {
    if (!this.definitionsCache$) {
      this.definitionsCache$ = this.http.get<any>(`${this.baseUrl}/definitions`).pipe(
        map((res) => (Array.isArray(res) ? res : (res?.content ?? []))),
        shareReplay(1)
      );
    }
    return this.definitionsCache$;
  }

  invalidateDefinitionsCache(): void {
    this.definitionsCache$ = null;
  }

  getLabelDefinition(uuid: string): Observable<LabelDefinition> {
    return this.http.get<LabelDefinition>(`${this.baseUrl}/definitions/${uuid}`);
  }

  createLabelDefinition(data: CreateLabelDefinitionRequest): Observable<LabelDefinition> {
    return this.http.post<LabelDefinition>(`${this.baseUrl}/definitions`, data);
  }

  updateLabelDefinition(uuid: string, data: Partial<CreateLabelDefinitionRequest>): Observable<LabelDefinition> {
    return this.http.put<LabelDefinition>(`${this.baseUrl}/definitions/${uuid}`, data);
  }

  deleteLabelDefinition(uuid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/definitions/${uuid}`);
  }

  // --- Labels ---

  createLabel(data: CreateLabelRequest): Observable<Label> {
    return this.http.post<Label>(`${this.baseUrl}/labels`, data);
  }

  updateLabel(uuid: string, data: Partial<CreateLabelRequest>): Observable<Label> {
    return this.http.put<Label>(`${this.baseUrl}/labels/${uuid}`, data);
  }

  deleteLabel(uuid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/labels/${uuid}`);
  }

  // --- Object Labels ---

  getObjectLabels(params?: {
    relatedObjectType?: string;
    labelUuids?: string;
    userUuid?: string;
    page?: number;
    size?: number;
  }): Observable<any> {
    return this.http.get(`${this.baseUrl}/object-labels`, { params: params as any });
  }

  getLabelsForObject(relatedObjectType: string, relatedObjectUuid: string): Observable<ObjectLabel[]> {
    return this.http.get<any>(`${this.baseUrl}/object-labels`, {
      params: { relatedObjectType, relatedObjectUuid }
    }).pipe(
      map((res) => (Array.isArray(res) ? res : (res?.content ?? [])))
    );
  }

  attachLabel(data: AttachLabelRequest): Observable<ObjectLabel> {
    return this.http.post<ObjectLabel>(`${this.baseUrl}/object-labels`, data);
  }

  detachLabel(objectLabelUuid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/object-labels/${objectLabelUuid}`);
  }

  // --- Seed defaults ---

  createDefaultDefinitions(): Observable<LabelDefinition[]> {
    this.invalidateDefinitionsCache();
    return this.getLabelDefinitions().pipe(
      switchMap((existing) => {
        const existingNames = new Set(existing.map((def) => def.name));
        const missing = DEFAULT_LABEL_DEFINITIONS.filter((def) => !existingNames.has(def.name));
        if (missing.length === 0) {
          return of([]);
        }
        return forkJoin(missing.map((def) => this.createLabelDefinition(def)));
      })
    );
  }
}
